<script setup>
    const menuOpen = ref(false);
    const toggleMenu = () => {
        menuOpen.value = !menuOpen.value;
    };

    // Libraries
    import { storeToRefs } from 'pinia';

    const { $listen, $unlisten } = useNuxtApp();

    import { useSitesStore } from '@/stores/sites';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { usePointsOfInterestStore } from '@/stores/points-of-interest';
    import { useToursStore } from '@/stores/tours';
    import { useTrailsStore } from '@/stores/trails';
    import { useEventsStore } from '@/stores/events';
    import { useSponsorsStore } from '@/stores/sponsors';
    import { usePartnersStore } from '@/stores/partners';
    import { useWindowStore } from '@/stores/window';
    import { useHelpersStore } from '@/stores/helpers';
    import { useUserStore } from '@/stores/user';
    import { useArchivesStore } from '@/stores/archives';
    import { useArchivesNeighborhoodsStore } from '@/stores/archives-neighborhoods';
    import { useNotificationsStore } from '@/stores/notifications';

    import { updateHead } from '@/composables/helpers/updateHead';
    // Was `checkTime()` from the now-deleted app/composables/fetchIntervals.ts (Task 10).
    const REFRESH_GATE_SECONDS = 45;
    const checkTime = (storeVal, currentVal) => Math.floor((currentVal - storeVal) / 1000) >= REFRESH_GATE_SECONDS;

    const frontEnd = useRuntimeConfig().public.clientFrontend;

    const route = useRoute();

    const SEO = {
        title: 'Chicago Architecture Center',
        description:
            "The Chicago Architecture Center's Open House Chicago is a FREE annual festival that typically offers access to hundreds of sites across Chicago, from iconic locations to under-the-radar architectural gems.",
        path: useRoute().path,
        image: frontEnd + '/img/ohc-share.jpg',
    };

    // Instantiate the stores
    const sitesStore = useSitesStore();
    const neighborhoodsStore = useNeighborhoodsStore();
    const eventsStore = useEventsStore();
    const partnersStore = usePartnersStore();
    const toursStore = useToursStore();
    const sponsorsStore = useSponsorsStore();
    const userStore = useUserStore();
    // not needed on app load
    const pointsOfInterestStore = usePointsOfInterestStore();
    // not needed on homepage
    const trailsStore = useTrailsStore();
    const archiveStore = useArchivesStore();
    const archiveNeighborhoodStore = useArchivesNeighborhoodsStore();
    const notificationStore = useNotificationsStore();

    updateHead(SEO);

    if (route.path.includes('archive')) {
        archiveStore.fetchArchives();
        archiveNeighborhoodStore.fetchArchivedNeighborhoods();
    } else {
        neighborhoodsStore.fetchNeighborhoods();
        sitesStore.fetchSites();
        eventsStore.fetchEvents();
        partnersStore.fetchPartners();
        toursStore.fetchTours();
        sponsorsStore.fetchSponsors();
        pointsOfInterestStore.fetchPointsOfInterest();
        trailsStore.fetchTrails();
    }

    const helpersStore = useHelpersStore();
    helpersStore.lastFetch = Date.now();

    const windowStore = useWindowStore();

    // Variables
    const { hasVisitedBefore, initialized } = storeToRefs(userStore);
    const { getUnreadCount, getNotificationsData } = storeToRefs(notificationStore);

    const pathsNoSitesRefresh = ['/sites/', '/sites', '/events', '/events/', '/my-ohc', '/my-ohc/', '/my-ohc/itineraries', '/my-ohc/itineraries/'];

    const pathsNoEventsRefresh = ['/sites/', '/sites', '/itineraries/', '/itineraries', '/neighborhoods'];
    const addToItineraryIsOpen = ref(false);
    const item = ref(null);
    const loading = ref(false);
    const siteNameForAlert = ref(null);
    const itineraryNameForAlert = ref(null);
    const showNotifications = ref(false);

    const siteAddedAlertIsOpen = ref(false);
    const itineraryAddedWithSiteAlertIsOpen = ref(false);

    const hasSrcDoc = ref(false);
    const srcdoc = ref('');

    // Methods
    const fetchNotifications = async () => {
        notificationStore.fetchNotifications();
    };

    const refreshUserData = async () => {
        userStore.refreshData();
    };

    function setSiteAndItineraryNames(values) {
        siteNameForAlert.value = values?.item?.fields?.Site || 'Site Name';
        itineraryNameForAlert.value = values?.selected?.name || 'Itinerary Name';
    }

    function openSiteAddedAlert(values) {
        siteAddedAlertIsOpen.value = true;
        setSiteAndItineraryNames(values);
    }
    function closeSiteAddedAlert() {
        siteAddedAlertIsOpen.value = false;
        siteNameForAlert.value = null;
        itineraryNameForAlert.value = null;
    }

    function openItineraryAddedWithSiteAlert(values) {
        addToItineraryIsOpen.value = false;
        itineraryAddedWithSiteAlertIsOpen.value = true;

        itineraryNameForAlert.value = values.itineraryName;
        siteNameForAlert.value = values.siteName;
    }

    function closeItineraryAddedWithSiteAlert() {
        itineraryAddedWithSiteAlertIsOpen.value = false;
        siteNameForAlert.value = null;
        itineraryNameForAlert.value = null;
    }

    const resize = () => {
        windowStore.windowHeight = window.innerHeight;
        windowStore.windowWidth = window.innerWidth;
    };

    const { data: footerHtml, error: footerError } = await useAsyncData('footer-html', () =>
        $fetch(`/api/footer`)
    );

    if (footerError.value) {
        console.error(footerError.value);
    } else {
        srcdoc.value = footerHtml.value?.html;
        hasSrcDoc.value = true;
    }

    let heightObserver = null;

    const handleFooterResize = () => {
        const iframe = document.getElementById('iframe-footer');
        if (!iframe?.contentWindow) return;
        const height = iframe.contentWindow.document.documentElement.offsetHeight;
        iframe.style.height = `${height + 1}px`;
    };

    const handleOpenGlobalItineraryModal = (val) => {
        item.value = val.value;
        addToItineraryIsOpen.value = true;
        document.body.style.overflow = 'hidden';
    };

    const handleCloseGlobalItineraryModal = () => {
        addToItineraryIsOpen.value = false;
        document.body.style.overflow = 'auto';
    };

    const handleAddedToItineraryAlert = (val2) => {
        openSiteAddedAlert(val2);
    };

    const handleCreatedItineraryWithSite = (val3) => {
        openItineraryAddedWithSiteAlert(val3);
        document.body.style.overflow = 'auto';
    };

    const handleShowNotifications = () => {
        showNotifications.value = true;
        document.body.style.overflow = 'hidden';
    };

    const handleHideNotifications = () => {
        notificationStore.markAsRead();
        showNotifications.value = false;
        document.body.style.overflow = 'auto';
    };

    const busHandlers = [
        ['openGlobalItineraryModal', handleOpenGlobalItineraryModal],
        ['closeGlobalItineraryModal', handleCloseGlobalItineraryModal],
        ['addedToItineraryAlert', handleAddedToItineraryAlert],
        ['createdItineraryWithSite', handleCreatedItineraryWithSite],
        ['showNotifications', handleShowNotifications],
        ['hideNotifications', handleHideNotifications],
    ];

    onMounted(() => {
        fetchNotifications();
        userStore.initUserData();

        if (import.meta.client) {
            resize();
            window.addEventListener('resize', resize);

            for (const [event, handler] of busHandlers) $listen(event, handler);

            const nativeHeight = document.documentElement.offsetHeight;
            window.parent.postMessage({ type: 'updateHeight', height: nativeHeight }, '*');

            // Add resizeObserver to update height
            heightObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    const { height } = entry.contentRect;
                    window.parent.postMessage({ type: 'updateHeight', height }, '*');
                }
            });

            heightObserver.observe(document.documentElement);

            window.addEventListener('resize', handleFooterResize);

            window.parent.postMessage({ type: 'tweakSEO' }, '*');
        }
    });

    // See layouts/default.vue: a layout unmounts when the app swaps to another
    // one, and mitt keeps a subscription until it is explicitly removed, so
    // every swap stacked another copy. This layout also left a ResizeObserver
    // attached to documentElement, which kept posting height messages to the
    // parent frame after the layout was gone.
    onUnmounted(() => {
        if (!import.meta.client) return;
        window.removeEventListener('resize', resize);
        window.removeEventListener('resize', handleFooterResize);
        if (heightObserver) {
            heightObserver.disconnect();
            heightObserver = null;
        }
        for (const [event, handler] of busHandlers) $unlisten(event, handler);
    });

    function closeEmailSignupModal(type) {
        userStore.updateHasVisitedBefore(true);
    }

    watch(
        () => route.path,
        async (to, from) => {
            if (to.includes('archive') && !archiveStore.sitesData?.length) {
                console.info('Fetching archived data...');
                loading.value = true;
                archiveStore.fetchArchives();
                archiveNeighborhoodStore.fetchArchivedNeighborhoods();
                loading.value = false;
            }
            if (!to.includes('archive') && !sitesStore.sitesData?.length) {
                console.info('Fetching current data...');
                loading.value = true;
                neighborhoodsStore.fetchNeighborhoods();
                sitesStore.fetchSites();
                eventsStore.fetchEvents();
                partnersStore.fetchPartners();
                toursStore.fetchTours();
                sponsorsStore.fetchSponsors();
                pointsOfInterestStore.fetchPointsOfInterest();
                trailsStore.fetchTrails();
                loading.value = false;
                helpersStore.lastFetch = Date.now();
            }
            const canRefetch = checkTime(helpersStore.lastFetch, Date.now());
            if (canRefetch) {
                if (!pathsNoSitesRefresh.includes(to)) sitesStore.fetchSites();
                if (!pathsNoEventsRefresh.includes(to)) eventsStore.fetchEvents();
                await trailsStore.fetchTrails();
                await pointsOfInterestStore.fetchPointsOfInterest();
                helpersStore.lastFetch = Date.now();
                fetchNotifications();
            }
            refreshUserData();
        },
        { deep: true }
    );

    const links = [
        // OHC 25'
        {
            title: 'OHC 2025',
            href: '/',
        },
        // Sites
        {
            title: 'Sites',
            href: '/sites',
        },
        // Areas
        {
            title: 'Neighborhood Areas',
            href: '/neighborhoods',
        },
        // OHC Tours
        {
            title: 'Itineraries',
            href: '/itineraries',
        },
        // Events
        {
            title: 'Events',
            href: '/events',
        },
        // My OHC
        {
            title: 'My OHC',
            href: '/my-ohc',
        },
        {
            title: 'Photo Competition',
            href: 'https://www.architecture.org/open-house-chicago/photo-competition-2026',
            target: '_blank',
            internal: true,
        },
        {
            title: 'Archives',
            href: '/archive',
        },
    ];

    const links_archive = [
        // OHC 24'
        {
            title: 'About OHC',
            href: 'https://www.architecture.org/open-house-chicago/about',
            target: '_blank',
            internal: true,
        },
        {
            title: 'Archives',
            href: '/archive',
        },
    ];

    const isFramed = () => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    };

    const sendToExternal = (ev) => {
        if (ev.target && ev.target.tagName === 'A') {
            const href = ev.target.getAttribute('href');
            if (isFramed()) {
                window.parent.postMessage({ type: 'navigateTo', href: href }, '*');
                ev.preventDefault();
                return false;
            }
        }
        return true;
    };

    function isActive(href) {
        let { path } = route;
        path = path.replace('/iframe', '');

        if (path === href) return true;
        if (path === href + '/') return true;

        if (path.startsWith(href) && href != '/') return true;
        if (path.startsWith(href + '/') && href != '/') return true;

        return false;
    }

    const getHeight = () => {
        if (import.meta.client) {
            const iframe = document.getElementById('iframe-footer');
            if (iframe && iframe.contentWindow && iframe.contentWindow.document && iframe.contentWindow.document.documentElement) {
                const height = iframe.contentWindow.document.documentElement.offsetHeight;
                iframe.style.height = `${height + 1}px`;
            }
        }
        return 0;
    };

</script>

<template>
    <div id="is-iframe">
        <div class="sticky-bar">
            <section class="iframe-sub-nav" v-if="links.length >= 1">
                <div class="container hd-container">
                    <button class="mobile-trigger" type="button" aria-label="Mobile Navigation Toggle Button" :class="{ showing: menuOpen }" @click="toggleMenu">
                        <span class="label">OHC Menu</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="19" viewBox="0 0 22 19" fill="none">
                            <path class="bar-1 bar" d="M1 1.5H21" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                            <path class="bar-2 bar" d="M1 9.5H21" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                            <path class="bar-3 bar" d="M1 17.5H21" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                    </button>
                    <nav class="hide-on-mobile" :class="`${menuOpen ? 'open' : 'hidden'}`">
                        <li v-for="link in links" :key="link.href">
                            <NuxtLink :to="link.href" :class="{ active: isActive(link.href) }" v-if="!link.target && !link.internal" @click="toggleMenu">{{ link.title }}</NuxtLink>
                            <a :href="link.href" :target="link.target" @click.native="sendToExternal" v-else-if="link.internal && link.target">{{ link.title }}</a>
                            <a :href="link.href" :target="link.target" v-else>{{ link.title }}</a>
                        </li>
                        <li class="login">
                            <NuxtLink to="/login" @click="toggleMenu">Login</NuxtLink>
                        </li>
                    </nav>
                </div>
            </section>
        </div>

        <Notifications v-if="showNotifications" :notifications="getNotificationsData" />

        <main id="main" class="is-iframe" ref="root" tabindex="-1">
            <div v-if="!loading">
                <NuxtPage class="page" />
            </div>
        </main>

        <BlocksEmailSignupAlert v-if="!hasVisitedBefore && initialized" @closeEmailModal="closeEmailSignupModal" />

        <GlobalAddToItinerary v-if="addToItineraryIsOpen" :item="item" />

        <ItinerariesAddedSiteAlert v-if="siteAddedAlertIsOpen" @closeAddedSiteAlert="closeSiteAddedAlert" :siteName="siteNameForAlert" :listName="itineraryNameForAlert" />

        <GlobalAddedItineraryWithSiteAlert
            v-if="itineraryAddedWithSiteAlertIsOpen"
            @closeAddedItineraryWithSite="closeItineraryAddedWithSiteAlert"
            :siteName="siteNameForAlert"
            :listName="itineraryNameForAlert" />

        <div id="footer-holder">
            <iframe v-if="hasSrcDoc" id="iframe-footer" title="Chicago Architecture Center footer" :srcdoc="srcdoc" onload="this.height = this.contentWindow.document.documentElement.offsetHeight"></iframe>
        </div>
    </div>
</template>

<style lang="scss">
    @use '@/assets/styles/main.scss';

    #is-iframe {
        display: flex;
        min-height: 100svh;
        flex-direction: column;
        --sticky-bar-height: 55px;
        
        @media (min-width: $tablet) {
            --sticky-bar-height: 52px;
        }

        main {
            flex: 1;
        }

        .sticky-bar {
            position: fixed;
            width: 100%;
            top: 0;
            z-index: 100;

            .iframe-sub-nav {
                background: $turquoise;
                color: white;

                .container {
                    justify-content: flex-start;
                    gap: 20px;
                    max-width: min(100%, 1150px);
                    margin: auto;

                    @media (max-width: $tablet) {
                        padding: 0 1rem;
                        max-width: min(100%);
                    }

                    nav {
                        list-style: none;
                        display: flex;
                        flex-wrap: wrap;
                        gap: 1.5rem;
                        padding: 1rem 0;

                        li {
                            a {
                                font-size: 18px;
                                font-weight: 700;
                                font-family: 'Avenir Next Condensed', 'Helvetica Neue', Helvetica, Arial, sans-serif;
                                text-align: center;
                                text-transform: uppercase;
                                transition: 300ms ease;
                                border-bottom: 2px solid transparent;
                                white-space: nowrap;

                                &:hover,
                                &.active {
                                    border-bottom: 2px solid white;
                                }
                            }

                            &.login {
                                margin-left: auto;
                                margin-right: 0;
                            }
                        }
                    }
                }
            }
        }

        #main {
            margin-top: var(--sticky-bar-height);
            padding-bottom: calc($bottom + env(safe-area-inset-bottom));
            min-height: 400px;

            @media (min-width: $tablet) {
                min-height: 516px;
            }

            &.is-iframe {
                padding-top: 0;
            }
        }

        .mobile-trigger {
            cursor: pointer;
            display: flex;
            gap: 1rem;
            padding: 1rem 0;
            border: none;
            margin: 0;
            position: relative;
            z-index: 1;
            overflow: hidden;

            @media (min-width: $tablet) {
                display: none;
            }

            .label {
                font-size: 18px;
                font-weight: 700;
                font-family: 'Avenir Next Condensed', 'Helvetica Neue', Helvetica, Arial, sans-serif;
                text-align: center;
                text-transform: uppercase;
                transition: 300ms ease;
                border-bottom: 2px solid transparent;
                white-space: nowrap;
            }

            svg {
                display: inline-block;
                width: 100%;
            }

            .bar {
                transition: all 0.25s linear;
                will-change: transform, opacity;
                transform: translate(0, 0) rotate(0deg);
            }

            &.showing {
                .bar-1 {
                    transform: translate(4px, 1px) rotate(45deg);
                }
                .bar-2 {
                    opacity: 0;
                }
                .bar-3 {
                    transform: translate(-9px, 5px) rotate(-45deg);
                }
            }
        }

        .add-to-itinerary {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            z-index: 1000;
        }

        #footer-holder {
            width: 100%;

            #iframe-footer {
                overflow: hidden;
                width: 100%;
            }
        }

        .hide-on-mobile {
            transform: translate(0, -100%);
            height: 1px;
            opacity: 0;
            user-select: none;
            pointer-events: none;
            padding: 0 !important;
            transition: opacity 0ms ease-in-out;

            &.open {
                transform: translate(0, 0);
                opacity: 1;
                pointer-events: all;
                user-select: auto;
                height: auto;
                padding: 1rem 0 !important;
                transition: opacity 500ms ease-in-out;
            }

            @media (min-width: $tablet) {
                transform: translate(0, 0);
                opacity: 1;
                user-select: auto !important;
                pointer-events: all !important;
                height: auto;
                padding: 1rem 0 !important;
                transition: opacity 500ms ease-in-out;
            }
        }

        @media (min-width: $tablet) {
            #main {
                &.is-iframe {
                    padding-top: 16px;
                    padding-bottom: 0;
                }
            }
        }
    }
</style>
